import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Country, IFrequency, PagingDataResponse, Status,
} from '../../types';
import { selectToken } from '../../utils';
import { RootState } from '../index';

export enum PlatformEnum {
  ios = 'iOS',
  android = 'Android',
}

export type IPlatform = keyof typeof PlatformEnum;

export interface Payment {
  createdAt: string;
  data: PaymentData | IOSNotificationData;
  platform: IPlatform;
}

export interface IOSSignedRenewalInfo {
  currency: string;
  productId: string;
  signedDate: string;
  environment: string;
  renewalDate: string;
  renewalPrice: number;
  autoRenewStatus: number;
  appTransactionId: string;
  autoRenewProductId: string;
  originalTransactionId: string;
  recentSubscriptionStartDate: string;
}

export interface IOSSignedTransactionInfo {
  type: string;
  price: number;
  bundleId: string;
  currency: string;
  quantity: number;
  productId: string;
  signedDate: string;
  storefront: string;
  environment: string;
  expiresDate: string;
  purchaseDate: string;
  storefrontId: string;
  transactionId: string;
  appTransactionId: string;
  transactionReason: string;
  inAppOwnershipType: string;
  webOrderLineItemId: string;
  originalPurchaseDate: string;
  originalTransactionId: string;
  subscriptionGroupIdentifier: string;
}

export interface IOSPaymentData {
  status: number;
  bundleId: string;
  appAppleId: number;
  environment: string;
  bundleVersion: string;
  signedRenewalInfo: IOSSignedRenewalInfo;
  signedTransactionInfo: IOSSignedTransactionInfo;
}

export interface IOSNotificationData {
  data: IOSPaymentData;
  subtype: string;
  version: string;
  signedDate: string;
  notificationType: string;
  notificationUUID: string;
}

export interface PaymentData {
  version: string;
  packageName: string;
  decryptedToken: DecryptedToken;
  eventTimeMillis: string;
  subscriptionNotification: SubscriptionNotification;
}

export interface DecryptedToken {
  payload: Payload;
  errorMessage: string | null;
  isSuccessful: boolean;
}

export interface Payload {
  kind: string;
  lineItems: Record<string, LineItem>;
  startTime: string;
  regionCode: string;
  testPurchase: object;
  latestOrderId: string;
  subscriptionState: string;
  acknowledgementState: string;
  externalAccountIdentifiers: ExternalAccountIdentifiers;
}

export interface LineItem {
  productId: string;
  expiryTime: string;
  offerDetails: OfferDetails;
  autoRenewingPlan: AutoRenewingPlan;
  latestSuccessfulOrderId: string;
}

export interface OfferDetails {
  basePlanId: string;
}

export interface AutoRenewingPlan {
  recurringPrice: RecurringPrice;
  autoRenewEnabled: boolean;
}

export interface RecurringPrice {
  nanos: number;
  units: string;
  currencyCode: string;
}

export interface ExternalAccountIdentifiers {
  obfuscatedExternalAccountId: string;
}

export interface SubscriptionNotification {
  version: string;
  purchaseToken: string;
  subscriptionId: string;
  notificationType: number;
}

export interface UserDetail {
  id: string,
  name?: string,
  email?: string,
  status?: string,
  lastActivityAt?: string,
  gender?: string,
  horses?: HorseAndProduct[],
  city?: string,
  address?: string,
  country?: Country,
  photoUrl?: string,
  dateOfBirth?: string,
  createdAt?: string,
  updatedAt?: string,
  calculatorUsedAt?: string,
  daysCount?: number,
  notesNumber?: number,
  workoutsNumber?: number,
  workoutLogsNumber?: number,
  noteLogsNumber?: number,
  subscription?: IFrequency;
  subscriptionTill?: string,
  platform?: string,
  mustSetupHorses?: boolean,
  payments?: Payment[]
}

export interface Users {
  id: string,
  name: string,
  email: string,
  status: Status,
  lastActivityAt: string,
  gender: string,
  horses: HorseAndProduct[],
  city: string,
  address: string,
  country: Country,
  createdAt: string,
  updatedAt: string,
  daysCount: number,
  subscription: IFrequency;
  subscriptionTill: string;
}

export type HorseAndProduct = {
  id?: string,
  name?: string,
  products?: UserProducts[],
}

export type UserProducts = {
  id?: string,
  nameEn?: string,
  nameNo?: string,
}

export interface UserGetParams {
  country?: string[],
  city?: string[],
  status?: string[],
  nutritionType?: string,
  users?: string[],
  page?: number[],
  limit?: string[],
  search?: string[],
  orderBy?: string[],
  orderByColumn?: string[],
}

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API,
    prepareHeaders: (headers, { getState }) => {
      const token = selectToken(getState() as RootState);

      if (token) {
        headers.set('Authorization', `Bearer ${token.token}`);
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    getUsersList: builder.query<PagingDataResponse<Users>, UserGetParams>({
      query: (params) => ({
        url: '/admin/users',
        method: 'GET',
        params,
      }),
    }),
    getUser: builder.mutation<UserDetail, string>({
      query: (id) => ({
        url: `/admin/users/${id}`,
        method: 'GET',
      }),
    }),
    editUser: builder.mutation<string, { formData: UserDetail, id: string}>({
      query: ({ formData, id }) => ({
        url: `/admin/users/${id}`,
        method: 'PATCH',
        body: formData,
      }),
    }),
    deleteUser: builder.mutation<string, string>({
      query: (id) => ({
        url: `/admin/users/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useLazyGetUsersListQuery,
  useEditUserMutation,
  useGetUserMutation,
  useDeleteUserMutation,
} = usersApi;
